import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEEPLINK_PARAMS } from '@/shared/constants/deeplinkParams';
import ROUTES from '@/shared/constants/routes';
import { openExternalUrl } from '@/shared/download';

export const SUPPORT_CHAT_DRAFTS: Record<string, string> = {
	'account-support': 'Hola, necesito soporte con mi cuenta en heru.',
	'service-inquiry': 'Hola, me interesa contratar un servicio de heru.',
	'fiscal-inquiry': 'Hola, necesito asesoría fiscal.',
};

export const supportChatPath = (entryPoint: string) =>
	`${ROUTES.SUPPORT}?source=${encodeURIComponent(entryPoint)}`;

/** `/support` opened on one conversation (`?conversationId=`), with the page's `source` for the `support_chat_*` events. */
export const supportConversationPath = (conversationId: string, entryPoint: string) =>
	`${ROUTES.SUPPORT}?${DEEPLINK_PARAMS.CONVERSATION_ID}=${encodeURIComponent(conversationId)}&source=${encodeURIComponent(entryPoint)}`;

export const useOpenSupportChat = () => {
	const navigate = useNavigate();
	return useCallback(
		(entryPoint: string, draft?: string) => navigate(supportChatPath(entryPoint), { state: { draft } }),
		[navigate],
	);
};

/**
 * Única fuente de los números de WhatsApp de soporte. `cuenta` atiende los
 * problemas de acceso y unir cuentas; `ventas` cotiza servicios; `asat` es la
 * línea de trámites ante el SAT de los socios de aliados (3843, Antonia).
 * Las dos van al número de soporte (4743): ahí Antonia escala a la bandeja de
 * Ventas o de Soporte según el caso. El 2377 queda solo para campañas (oct-2026).
 */
export const SUPPORT_WHATSAPP_NUMBERS = {
	cuenta: '5215620054743',
	ventas: '5215620054743',
	asat: '5215620073843',
} as const;

export type SupportWhatsAppLine = keyof typeof SUPPORT_WHATSAPP_NUMBERS;

export const openSupportWhatsApp = (line: SupportWhatsAppLine, message: string) => {
	openExternalUrl(`https://wa.me/${SUPPORT_WHATSAPP_NUMBERS[line]}?text=${encodeURIComponent(message)}`);
};

/**
 * Cómo se contacta a un especialista desde una pantalla compartida. La
 * pantalla no decide el canal: quien la monta sabe si está dentro del
 * onboarding (donde navegar al chat rebota a `/onboarding`) y le pasa uno.
 */
export type SpecialistChannel = 'chat' | 'whatsapp';

export type SpecialistContact = {
	channel: SpecialistChannel;
	open: (entryPoint: string, draft: string) => void;
};

/** Nombre del canal tal como lo lee el usuario en los textos. */
export const SPECIALIST_CHANNEL_LABEL: Record<SpecialistChannel, string> = {
	chat: 'chat',
	whatsapp: 'WhatsApp',
};

/** Dentro del onboarding: WhatsApp de ventas, sin salir de `/onboarding`. */
export const WHATSAPP_SPECIALIST_CONTACT: SpecialistContact = {
	channel: 'whatsapp',
	open: (_entryPoint, draft) => openSupportWhatsApp('ventas', draft),
};

/** Fuera del onboarding: el chat de la app con su `entry_point` y borrador. */
export const useChatSpecialistContact = (): SpecialistContact => {
	const openSupportChat = useOpenSupportChat();
	return useMemo(() => ({ channel: 'chat', open: openSupportChat }), [openSupportChat]);
};
